/**
 * Labstep
 *
 * @module components/Entity/Action/Update
 * @desc An action component for updating an entity
 */

import { EntityCanUpdateContainer } from 'labstep-web/containers/Entity/Update/Can';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import { enhanceOptions } from 'labstep-web/services/utils.service';
import type React from 'react';
import type {
  EntityActionUpdateContainerProps,
  EntityActionUpdateProps,
} from './types';

export const EntityActionUpdate: React.FC<
  EntityActionUpdateProps
> = ({
  status,
  body,
  update,
  options,
  actionComponentProps,
  optimistic,
}) => (
  <ActionComponent
    type="option"
    onClick={(additionalOptions): void =>
      update(body, enhanceOptions({ options, ...additionalOptions }))
    }
    status={optimistic ? undefined : status}
    dataTestId="entity-action-update"
    {...actionComponentProps}
  />
);

export const EntityActionUpdateContainer: React.FC<
  EntityActionUpdateContainerProps
> = ({
  entityName,
  id,
  optimistic,
  actionComponentProps,
  ...rest
}) => {
  const renderIfNoEditPermissions =
    typeof actionComponentProps === 'function';
  return (
    <EntityCanUpdateContainer
      entityName={entityName}
      id={id}
      optimistic={optimistic}
      renderIfNoEditPermissions={renderIfNoEditPermissions}
      field={Object.keys(rest.body)}
    >
      {({ status, update, canEdit }): React.ReactElement => (
        <EntityActionUpdate
          update={update}
          status={status}
          optimistic={optimistic}
          actionComponentProps={
            renderIfNoEditPermissions
              ? actionComponentProps({ canEdit })
              : actionComponentProps
          }
          {...rest}
        />
      )}
    </EntityCanUpdateContainer>
  );
};
